import React, { useState, useEffect } from 'react';
import { Modal, Button, Input } from '@/components/ui';
import type { MaintenanceCost, CostType, CostStatus } from '../types/maintenance-costs.types';
import { useCosts } from '../hooks/useCosts';
import { toast } from 'sonner';

interface CostFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  item?: MaintenanceCost | null;
  companyId: string;
}

const typeOptions: { value: CostType; label: string }[] = [
  { value: 'labor', label: 'Nhân công' },
  { value: 'material', label: 'Vật tư' },
  { value: 'equipment', label: 'Thiết bị' },
  { value: 'other', label: 'Khác' },
];

export function CostFormModal({ isOpen, onClose, item, companyId }: CostFormModalProps) {
  const isEdit = !!item;
  const { createCost, isCreating } = useCosts(companyId);

  const [formData, setFormData] = useState<Partial<MaintenanceCost>>({
    cost_date: '',
    cost_type: 'material',
    category: '',
    description: '',
    quantity: 1,
    unit_price: 0,
    total_amount: 0,
    currency: 'VND',
    status_code: 'pending',
    project_id: '',
    work_order_id: '',
  });

  useEffect(() => {
    if (isOpen) {
      if (item) {
        setFormData(item);
      } else {
        setFormData({
          cost_date: new Date().toISOString().split('T')[0],
          cost_type: 'material',
          category: '',
          description: '',
          quantity: 1,
          unit_price: 0,
          total_amount: 0,
          currency: 'VND',
          status_code: 'pending',
          project_id: '',
          work_order_id: '',
          company_id: companyId,
        });
      }
    }
  }, [isOpen, item, companyId]);

  const handlePriceChange = (quantity: number, price: number) => {
    setFormData({
      ...formData,
      quantity,
      unit_price: price,
      total_amount: quantity * price
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!isEdit) {
        await createCost({ ...formData, company_id: companyId });
      }
      onClose();
      toast.success(isEdit ? 'Cập nhật chi phí thành công' : 'Ghi nhận chi phí mới thành công');
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Cập nhật khoản chi phí' : 'Ghi nhận chi phí bảo dưỡng mới'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6 py-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Mục chi phí *"
            required
            value={formData.category || ''}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            placeholder="VD: Mua phân bón NPK"
          />
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Loại chi phí *</label>
            <select
              required
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.cost_type || ''}
              onChange={(e) => setFormData({ ...formData, cost_type: e.target.value as CostType })}
            >
              {typeOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Ngày ghi nhận *"
            type="date"
            required
            value={formData.cost_date || ''}
            onChange={(e) => setFormData({ ...formData, cost_date: e.target.value })}
          />
          <Input
            label="Mã tham chiếu (Hóa đơn)"
            value={formData.reference_code || ''}
            onChange={(e) => setFormData({ ...formData, reference_code: e.target.value })}
            placeholder="VD: INV-2024-001"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="Số lượng *"
            type="number"
            required
            value={formData.quantity || 1}
            onChange={(e) => handlePriceChange(parseFloat(e.target.value), formData.unit_price || 0)}
          />
          <Input
            label="Đơn giá *"
            type="number"
            required
            value={formData.unit_price || 0}
            onChange={(e) => handlePriceChange(formData.quantity || 1, parseFloat(e.target.value))}
          />
          <Input
            label="Tổng tiền"
            disabled
            value={new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(formData.total_amount || 0)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="ID Dự án *"
            required
            value={formData.project_id || ''}
            onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
          />
          <Input
            label="ID Lệnh công việc (Nếu có)"
            value={formData.work_order_id || ''}
            onChange={(e) => setFormData({ ...formData, work_order_id: e.target.value })}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Ghi chú chi tiết</label>
          <textarea
            className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-3 min-h-[80px]"
            placeholder="Mục đích chi, tình trạng hóa đơn..."
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-50">
          <Button variant="outline" type="button" onClick={onClose} disabled={isCreating}>Hủy</Button>
          <Button variant="primary" type="submit" isLoading={isCreating}>
            {isEdit ? 'Lưu thay đổi' : 'Ghi nhận chi phí'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
